import { useEffect, useRef, useState } from 'react'
import { Send, Paperclip, CheckCircle2, Loader2, MessageSquare, Phone } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import EmptyState from '@/components/common/EmptyState'
import VoicePanelMigracion from './VoicePanelMigracion'

/**
 * Espejo LIVIANO, solo para UI, de `api/src/services/cd2bMigracionChatPerfiles.js` — el
 * frontend no puede importar el módulo del backend, así que se replica aquí únicamente lo que
 * la vista necesita. Mismo perfil que ya usa `portal-nextjs/.../ChatMigracion.tsx` (versión
 * pública) — mantener ambos en sincronía si se agrega una entidad nueva.
 */
const PERFILES_UI = {
  productos: {
    campoNombre: 'nombre',
    camposCriticos: ['precio'],
    tituloVacio: 'Platícale al agente',
    descripcionVacia: 'Dile qué productos tiene el negocio y a cuánto los vende — algo como "tengo martillos a $5 y clavos a $2".',
    columnaDinero: true,
    etiquetaCatalogo: 'Catálogo en vivo',
  },
  clientes: {
    campoNombre: 'nombre',
    camposCriticos: [],
    tituloVacio: 'Platícale al agente',
    descripcionVacia: 'Dile qué clientes tiene el negocio — algo como "el cliente es Ferretería El Sol, NIT 0614-123456-001-2".',
    columnaDinero: false,
    etiquetaCatalogo: 'Clientes en vivo',
  },
  proveedores: {
    campoNombre: 'nombre',
    camposCriticos: [],
    tituloVacio: 'Platícale al agente',
    descripcionVacia: 'Dile a quién le compra el negocio — algo como "le compra a Distribuidora Central, su NIT es 0614-987654-002-1".',
    columnaDinero: false,
    etiquetaCatalogo: 'Proveedores en vivo',
  },
  empleados: {
    campoNombre: 'nombre',
    camposCriticos: [],
    tituloVacio: 'Platícale al agente',
    descripcionVacia: 'Dile quién trabaja en el negocio — algo como "Ana María Pérez es cajera, DUI 01234567-8, gana $400 al mes".',
    columnaDinero: false,
    etiquetaCatalogo: 'Empleados en vivo',
  },
  categorias: {
    campoNombre: 'nombre',
    camposCriticos: [],
    tituloVacio: 'Platícale al agente',
    descripcionVacia: 'Dile cómo agrupa el catálogo — algo como "tengo las categorías Ferretería y Tornillería".',
    columnaDinero: false,
    etiquetaCatalogo: 'Categorías en vivo',
  },
  cuentasBancarias: {
    campoNombre: 'nombre',
    camposCriticos: [],
    tituloVacio: 'Platícale al agente',
    descripcionVacia: 'Dile qué cuentas de banco tiene el negocio — algo como "cuenta corriente en Banco Agrícola, número 001-234567-8, con $12,500".',
    columnaDinero: false,
    etiquetaCatalogo: 'Cuentas en vivo',
  },
  ubicaciones: {
    campoNombre: 'nombre',
    camposCriticos: [],
    tituloVacio: 'Platícale al agente',
    descripcionVacia: 'Dile qué bodegas o sucursales tiene el negocio — algo como "tengo la Bodega central y la Sucursal Santa Ana".',
    columnaDinero: false,
    etiquetaCatalogo: 'Ubicaciones en vivo',
  },
}

/** ¿A esta fila le falta algún campo crítico del perfil? Mismo criterio que `MigrationChatAgent.faltaAlgunCritico` en el backend. */
function faltaAlgunCritico(fila, camposCriticos) {
  return camposCriticos.some((c) => {
    const v = fila?.[c]
    return v == null || v === '' || (typeof v === 'number' && v < 0)
  })
}

function estadoDe(fila, camposCriticos) {
  if (faltaAlgunCritico(fila, camposCriticos)) return { etiqueta: camposCriticos.includes('precio') ? 'Falta precio' : 'Incompleto', variante: 'warning' }
  if (fila.origenDato === 'sugerido_mercado') return { etiqueta: 'Sugerido', variante: 'secondary' }
  return { etiqueta: 'Listo', variante: 'success' }
}

/**
 * EL CHAT — el asesor platica el catálogo en vez de mapear un Excel.
 *
 * Dos columnas: la conversación a la izquierda, el catálogo llenándose EN VIVO a la derecha —
 * es literalmente lo que va a quedar en el proyecto, no una previsualización aparte. Nada de
 * lo que se ve aquí está escrito todavía: solo `onConfirmar` materializa filas de verdad.
 */
export default function ChatMigracion({
  proyectoId,
  hojaId,
  entidad = 'productos',
  chat,
  catalogoEnVivo = [],
  onEnviarMensaje,
  onSubirFoto,
  onConfirmar,
  onRecargarEstado,
  enviando,
  confirmando,
}) {
  const [texto, setTexto] = useState('')
  const [modoConversacion, setModoConversacion] = useState('texto')
  const bodyRef = useRef(null)
  const fileRef = useRef(null)
  const mensajes = chat?.mensajes || []
  const perfil = PERFILES_UI[entidad] || PERFILES_UI.productos
  // El modo voz lee productos dictados por Gemini Live — sin equivalente todavía para otras entidades.
  const soportaVoz = entidad === 'productos'

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight
  }, [mensajes.length])

  const enviar = () => {
    const t = texto.trim()
    if (!t || enviando) return
    setTexto('')
    onEnviarMensaje?.(t)
  }

  const listos = catalogoEnVivo.filter((f) => !faltaAlgunCritico(f, perfil.camposCriticos)).length
  const puedeConfirmar = catalogoEnVivo.length > 0 && listos === catalogoEnVivo.length && !enviando && !confirmando

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,20rem)]">
      <div className="space-y-3">
        {soportaVoz && (
          <div className="inline-flex rounded-md border border-neutral-200 dark:border-neutral-800 p-1">
            <button
              type="button"
              onClick={() => setModoConversacion('texto')}
              className={cn(
                'flex items-center gap-1.5 rounded px-3 py-1.5 text-body-sm font-medium transition-colors',
                modoConversacion === 'texto' ? 'bg-primary text-white' : 'text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50'
              )}
            >
              <MessageSquare size={14} /> Texto
            </button>
            <button
              type="button"
              onClick={() => setModoConversacion('voz')}
              className={cn(
                'flex items-center gap-1.5 rounded px-3 py-1.5 text-body-sm font-medium transition-colors',
                modoConversacion === 'voz' ? 'bg-primary text-white' : 'text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50'
              )}
            >
              <Phone size={14} /> Voz
            </button>
          </div>
        )}

        {soportaVoz && modoConversacion === 'voz' ? (
          <VoicePanelMigracion proyectoId={proyectoId} hojaId={hojaId} onCatalogoActualizado={onRecargarEstado} />
        ) : (
          <Card className="flex h-[32rem] flex-col">
            <div ref={bodyRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
              {mensajes.length === 0 ? (
                <EmptyState titulo={perfil.tituloVacio} descripcion={perfil.descripcionVacia} />
              ) : (
                mensajes.map((m, i) => (
                  <div
                    key={i}
                    className={cn(
                      'max-w-[85%] rounded-lg px-3 py-2 text-body-sm',
                      m.rol === 'asesor' ? 'ml-auto bg-primary text-white' : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-50'
                    )}
                  >
                    {m.texto}
                  </div>
                ))
              )}
              {enviando && (
                <div className="flex items-center gap-1.5 text-caption text-neutral-400 dark:text-neutral-500">
                  <Loader2 size={13} className="animate-spin" /> escribiendo…
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 border-t border-neutral-200 dark:border-neutral-800 p-3">
              {entidad === 'productos' && (
                <>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0]
                      if (f) onSubirFoto?.(f)
                      e.target.value = ''
                    }}
                  />
                  <Button variant="ghost" size="icon-sm" onClick={() => fileRef.current?.click()} disabled={enviando} title="Adjuntar foto">
                    <Paperclip size={16} />
                  </Button>
                </>
              )}
              <input
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), enviar())}
                placeholder="Escribe aquí…"
                disabled={enviando}
                className="h-10 min-w-0 flex-1 rounded-md border border-line bg-card px-3 text-body-sm text-foreground placeholder:text-neutral-400 dark:placeholder:text-neutral-500 focus:border-primary focus:outline-none"
              />
              <Button size="icon-sm" onClick={enviar} disabled={enviando || !texto.trim()}>
                <Send size={16} />
              </Button>
            </div>
          </Card>
        )}
      </div>

      <Card>
        <CardContent className="space-y-3 py-4">
          <div className="flex items-center justify-between">
            <p className="text-caption font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">{perfil.etiquetaCatalogo}</p>
            <p className="text-caption text-neutral-500 dark:text-neutral-400">
              {listos} de {catalogoEnVivo.length}
            </p>
          </div>

          {catalogoEnVivo.length === 0 ? (
            <p className="text-caption text-neutral-400 dark:text-neutral-500">Aquí van apareciendo según conversas.</p>
          ) : (
            <ul className="space-y-2">
              {catalogoEnVivo.map((f, i) => {
                const estado = estadoDe(f, perfil.camposCriticos)
                const nombre = f[perfil.campoNombre]
                return (
                  <li key={nombre || i} className="flex items-center gap-2">
                    <span className="flex-1 truncate text-body-sm text-neutral-800 dark:text-neutral-200">{nombre}</span>
                    {perfil.columnaDinero && f.precio > 0 && (
                      <span className="text-caption tabular-nums text-neutral-500 dark:text-neutral-400">${f.precio.toFixed(2)}</span>
                    )}
                    <Badge variant={estado.variante}>{estado.etiqueta}</Badge>
                  </li>
                )
              })}
            </ul>
          )}

          <Button className="w-full" onClick={onConfirmar} disabled={!puedeConfirmar}>
            {confirmando ? (
              <>
                <Loader2 size={15} className="mr-1.5 animate-spin" /> Confirmando…
              </>
            ) : (
              <>
                <CheckCircle2 size={15} className="mr-1.5" /> Confirmar catálogo
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}
